import { GenericField, InputProps } from "../Fields/GenericField";
import { useField } from "formik";
import { ComponentProps, KeyboardEvent, useState, useRef } from "react";
import { InputGroup } from "@/components/catalyst/input";
import { clsx } from "clsx";

export type EmailListInputProps = InputProps & {
  inputProps?: ComponentProps<"input">;
  onChange?: (emails: string[]) => void;
  placeholder?: string;
  validateEmail?: (email: string) => boolean;
};

// Default email validation function
const defaultEmailValidation = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export function MailToInput({
  inputProps,
  onChange,
  placeholder = "Enter email addresses...",
  validateEmail = defaultEmailValidation,
  autoFocus,
  ...props
}: EmailListInputProps) {
  const { emptyValue = [] } = props;
  const [field, meta, { setValue }] = useField(props.name);
  const [inputValue, setInputValue] = useState("");
  const [invalidEmails, setInvalidEmails] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const showError = !!meta.error && meta.touched;

  const emails: string[] = field.value ?? emptyValue;
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    // Clear invalid emails when user starts typing again
    if (invalidEmails.size > 0) {
      setInvalidEmails(new Set());
    }
  };
  const addEmail = async (email: string) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    // Validate email
    if (!validateEmail(trimmedEmail)) {
      setInvalidEmails((prev) => new Set(prev).add(trimmedEmail));
      return;
    }

    // Remove from invalid emails if it was there
    setInvalidEmails((prev) => {
      const newSet = new Set(prev);
      newSet.delete(trimmedEmail);
      return newSet;
    });

    const newEmails = [...emails, trimmedEmail];
    await setValue(newEmails);
    onChange?.(newEmails);
    setInputValue("");
  };

  const removeEmail = async (emailToRemove: string) => {
    const newEmails = emails.filter((email) => email !== emailToRemove);
    await setValue(newEmails);
    onChange?.(newEmails);

    // Also remove from invalid emails if it's there
    setInvalidEmails((prev) => {
      const newSet = new Set(prev);
      newSet.delete(emailToRemove);
      return newSet;
    });
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" || event.key === "," || event.key === ";") {
      // Don't prevent default for Tab if there's no input value to avoid breaking form navigation
      if (event.key === "Tab" && !inputValue.trim()) {
        return;
      }
      event.preventDefault();
      await addEmail(inputValue);
    } else if (event.key === "Backspace" && !inputValue && emails.length > 0) {
      // Remove last email if backspace is pressed and input is empty
      await removeEmail(emails[emails.length - 1]);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailsFromPaste = pastedText
      .split(/[,;\s\n]+/)
      .filter((email) => email.trim());

    emailsFromPaste.forEach((email) => addEmail(email));
  };

  const viewValue = emails.length > 0 ? emails.join(", ") : "No emails";

  return (
    <GenericField
      viewNode={<span>{viewValue}</span>}
      description="Press Tab, comma, or semicolon to add emails. Paste multiple emails to add them all at once."
      {...props}
    >
      {() => (
        <div className="relative">
          <InputGroup>
            <span
              data-slot="control"
              className={clsx([
                // Basic layout - matching Input component
                "relative block w-full",
                // Background color + shadow applied to inset pseudo element
                "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
                // Background color is moved to control and shadow is removed in dark mode
                "dark:before:hidden",
                // Focus ring - matching Input component
                "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent focus-within:after:ring-2 focus-within:after:ring-blue-500 sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
                // Invalid state
                showError && "before:shadow-red-500/10",
              ])}
            >
              <div
                className={clsx([
                  // Layout matching Input component
                  "relative block w-full appearance-none rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]",
                  // Typography - matching Input component
                  "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white",
                  // Border - matching Input component
                  "border border-zinc-950/10 dark:border-white/10",
                  "hover:border-zinc-950/20 dark:hover:border-white/20",
                  // Background color - matching Input component
                  "bg-transparent dark:bg-white/5",
                  // Focus styles
                  "focus-within:outline-hidden",
                  // Invalid state border
                  showError &&
                    "border-red-500 hover:border-red-500 dark:border-red-500 dark:hover:border-red-500",
                  // Flex layout for chips and input
                  "flex min-h-[2.75rem] cursor-text flex-wrap items-center gap-1 sm:min-h-[2.25rem]",
                ])}
                onClick={() => inputRef.current?.focus()}
              >
                {/* Email chips */}
                {emails.map((email, index) => (
                  <div
                    key={`${email}-${index}`}
                    className={`inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs ${
                      invalidEmails.has(email)
                        ? "border border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                        : "border border-blue-200 bg-blue-100 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    }`}
                  >
                    <span>{email}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEmail(email);
                      }}
                      className={`ml-1 rounded-full p-0.5 transition-colors ${
                        invalidEmails.has(email)
                          ? "hover:bg-red-200 dark:hover:bg-red-800"
                          : "hover:bg-blue-200 dark:hover:bg-blue-800"
                      }`}
                      aria-label={`Remove ${email}`}
                    >
                      <svg
                        className="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                ))}

                {/* Input field */}
                <input
                  ref={inputRef}
                  id={field.name}
                  type="email"
                  value={inputValue}
                  placeholder={emails.length === 0 ? placeholder : ""}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  onPaste={handlePaste}
                  data-invalid={showError}
                  className="min-w-[120px] flex-1 border-none bg-transparent outline-none placeholder:text-zinc-500 dark:placeholder:text-zinc-400"
                  {...inputProps}
                  autoFocus={autoFocus}
                />
              </div>
            </span>
          </InputGroup>

          {/* Invalid emails help text - only show if there are invalid emails and no formik errors */}
          {invalidEmails.size > 0 && !showError && (
            <div className="mt-1 text-xs text-red-600 dark:text-red-400">
              Invalid email format: {Array.from(invalidEmails).join(", ")}
            </div>
          )}
        </div>
      )}
    </GenericField>
  );
}
